<template>
	<BaseModal :title="t('title')" v-model="model" noPadding>
		<PagePillRow class="!scroll-pl-5 *:!px-5">
			<BaseButton
				color="secondary"
				size="md"
				@click="cases.toggleSort"
				class="snap-start"
			>
				<SortIcon
					class="h-5 w-5 transition-transform"
					:class="{ 'rotate-180': !cases.sortExpensive }"
				/>
				<div>{{ sortText }}</div>
			</BaseButton>
			<div class="bg-separator-secondary mx-2 h-4 w-px"></div>
			<TagGroup
				class="*:snap-start"
				v-model="cases.selectedTag"
				:tags="filters"
			/>
		</PagePillRow>
		<div
			class="via-fill-tertiary to-fill-tertiary sticky -top-16 z-1 bg-linear-to-t from-transparent px-5 py-4"
		>
			<SearchInput v-model="cases.search" />
		</div>
		<div
			class="grid grid-cols-2 gap-1.5 px-5 pb-4 min-[480px]:grid-cols-3 min-[720px]:grid-cols-4 min-[960px]:grid-cols-5"
		>
			<RouterLink
				:to="`/case/${item.uuid}`"
				custom
				v-slot="{ navigate }"
				v-for="item in cases.sorted"
			>
				<BaseCard
					:risk="item.risk"
					:price="item.price"
					:uuid="item.uuid"
					:name="item.name"
					class="border-fill-secondary rounded-xl border"
					v-hover="{ active: 'scale-95', impact: 'medium' }"
				>
					<div
						class="relative flex flex-col items-center px-2 pt-4 pb-2"
						@click="navigate"
					>
						<div
							class="text-label-tertiary truncate text-center text-xs font-medium uppercase"
						>
							{{ item.name }}
						</div>
						<CoinPrice class="mt-1.5" :price="item.price" />
						<div
							class="relative -mt-3 ml-2 flex aspect-square w-full items-center justify-center px-1.5"
						>
							<LazyImage
								:src="caseIcon(item.uuid)"
								:alt="item.name"
								class="max-h-50 max-w-50"
							/>
						</div>
						<RiskBar :value="item.risk" class="-mt-3 w-full" />
						<div
							class="bg-fill-secondary mt-4 flex h-11 w-full items-center justify-center overflow-hidden rounded-lg px-3"
							@click.stop="addToSelection(item.uuid)"
							@touchstart.stop
						>
							<Transition
								enterActiveClass="transition-transform"
								leaveActiveClass="transition-transform"
								enterFromClass="-translate-x-10"
								leaveToClass="-translate-x-10"
							>
								<div
									class="relative after:absolute after:-inset-3"
									@click.stop="removeFromSelection(item.uuid, $event)"
									@touchstart.stop
									v-if="battles.selectedCases.includes(item.uuid)"
								>
									<MinusFillIcon class="text-label-tertiary h-5 w-5 shrink-0" />
								</div>
							</Transition>
							<div
								class="text-label-secondary flex-1 text-center text-sm font-semibold uppercase"
							>
								{{
									!battles.selectedCases.includes(item.uuid)
										? t('add')
										: battles.selectedCases.filter((uuid) => uuid === item.uuid)
												.length
								}}
							</div>
							<Transition
								enterActiveClass="transition-transform"
								leaveActiveClass="transition-transform"
								enterFromClass="translate-x-10"
								leaveToClass="translate-x-10"
							>
								<div
									class="relative after:absolute after:-inset-3"
									@click.stop="addToSelection(item.uuid, $event, true)"
									@touchstart.stop
									v-if="battles.selectedCases.includes(item.uuid)"
								>
									<PlusFillIcon class="text-label-tertiary h-5 w-5 shrink-0" />
								</div>
							</Transition>
						</div>
					</div>
				</BaseCard>
			</RouterLink>
		</div>
		<BottomActionsModal class="pt-4">
			<CountAndTotal :count="battles.selectedCases.length" :total="amount">
				{{ t('rounds', battles.selectedCases.length) }}
			</CountAndTotal>
			<BaseButton
				size="lg"
				color="primary"
				class="relative mt-3 w-full"
				@click="confirm()"
			>
				{{ t('confirm') }}
			</BaseButton>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import PagePillRow from '../PagePillRow.vue'
import TagGroup from '../TagGroup.vue'
import SortIcon from '@/assets/icons/sort.svg'
import AllIcon from '@/assets/icons/all.svg'
import LowRiskIcon from '@/assets/icons/low-risk.svg'
import MediumRiskIcon from '@/assets/icons/medium-risk.svg'
import HighRiskIcon from '@/assets/icons/high-risk.svg'
import { useCases } from '@/stores/cases'
import PagePadding from '../PagePadding.vue'
import CaseCard from '../CaseCard.vue'
import SearchInput from '../SearchInput.vue'
import MinusFillIcon from '@/assets/icons/minus-fill.svg'
import PlusFillIcon from '@/assets/icons/plus-fill.svg'
import { useBattles } from '@/stores/battles'
import CoinPrice from '../CoinPrice.vue'
import { useI18n } from 'vue-i18n'
import BottomActionsModal from '../BottomActionsModal.vue'
import CaseInner from '../CaseInner.vue'
import RiskBar from '../RiskBar.vue'
import BaseCard from '../BaseCard.vue'
import LazyImage from '../LazyImage.vue'
import { caseIcon } from '@/utils/urls'
import CountAndTotal from '../CountAndTotal.vue'

const { t } = useI18n({
	messages: {
		en: {
			title: 'add cases',
			add: 'add',
			confirm: 'confirm',
			rounds: '{n} round | {n} rounds',
			filters: {
				all: 'All cases',
				low: 'Low risk',
				medium: 'Medium risk',
				high: 'High risk'
			},
			sort: {
				expensive: 'Expensive first',
				cheap: 'Cheap first'
			},
			total: 'total'
		},
		ru: {
			title: 'добавить кейсы',
			add: 'Выбрать',
			confirm: 'Подтвердить',
			rounds: '{n} раундов | {n} раунд | {n} раунда | {n} раундов',
			filters: {
				all: 'Все кейсы',
				low: 'Низкий риск',
				medium: 'Средний риск',
				high: 'Высокий риск'
			},
			sort: {
				expensive: 'Сначала дорогие',
				cheap: 'Сначала дешевые'
			},
			total: 'сумма'
		}
	}
})

const filters = computed(() => [
	{ text: t('filters.all'), icon: AllIcon },
	{ text: t('filters.low'), icon: LowRiskIcon },
	{ text: t('filters.medium'), icon: MediumRiskIcon },
	{ text: t('filters.high'), icon: HighRiskIcon }
])

const model = defineModel<boolean>()

const cases = useCases()
const battles = useBattles()

const amount = computed(() =>
	battles.selectedCases.reduce((acc, uuid) => {
		const item = cases.list.find((i) => i.uuid === uuid)
		return acc + (item ? item.price : 0)
	}, 0)
)

const confirm = () => {
	model.value = false
}

const addToSelection = (uuid: string, e?: Event, force?: boolean) => {
	if (battles.selectedCases.includes(uuid) && !force) {
		return
	}

	battles.addCaseToSelection(uuid)

	if (!e) {
		return
	}

	e.stopPropagation()
	e.stopImmediatePropagation()
}

const removeFromSelection = (uuid: string, e: Event) => {
	battles.removeCaseFromSelection(uuid)

	e.stopPropagation()
	e.stopImmediatePropagation()
}

const sortText = computed(() => {
	return cases.sortExpensive ? t('sort.expensive') : t('sort.cheap')
})
</script>
